import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Paper,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import { usePatientData } from '../context/PatientDataContext';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchPatients, fetchAdminScanStats } from '../api';

/**
 * Adjusts and formats UTC timestamps that were incorrectly stored 
 * (Eastern time stored as if it were UTC)
 * 
 * @param {string} dateStr - ISO date string from backend
 * @param {object} formatOptions - Options for date formatting
 * @param {boolean} timeOnly - If true, only returns time portion
 * @returns {string} Correctly formatted date/time in Eastern timezone
 */
const formatFixedEastern = (dateStr, formatOptions = {}, timeOnly = false) => {
  if (!dateStr) return '';
  
  const date = new Date(dateStr);
  
  // Determine if date is within US Eastern Daylight Time
  // DST begins on second Sunday in March and ends on first Sunday in November
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // Jan = 1, Dec = 12
  const day = date.getUTCDate();
  
  // Check if date is within DST period
  let isDST = false;
  
  if (month > 3 && month < 11) {
    // April through October are definitely DST
    isDST = true;
  } else if (month === 3) {
    // March - DST starts on second Sunday
    const secondSunday = 8 + (7 - new Date(year, 2, 8).getDay()) % 7;
    isDST = day >= secondSunday;
  } else if (month === 11) {
    // November - DST ends on first Sunday
    const firstSunday = 1 + (7 - new Date(year, 10, 1).getDay()) % 7;
    isDST = day < firstSunday;
  }
  
  // Add offset (4 hours during DST, 5 hours during standard time)
  const correctedDate = new Date(date.getTime() + (isDST ? 4 : 5) * 60 * 60 * 1000);
  
  // Format with user's locale (defaulting to en-US)
  const options = {
    timeZone: 'America/New_York',
    ...formatOptions
  };
  
  if (timeOnly) {
    return correctedDate.toLocaleTimeString('en-US', options);
  }
  
  return correctedDate.toLocaleString('en-US', options);
};

const PatientDatabase = ({ user }) => {
  const navigate = useNavigate();
  const { patients, setPatients } = usePatientData();
  const [adminScanStats, setAdminScanStats] = useState(null);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSanduke, setShowSanduke] = useState(false);

  const isPrivilegedUser = useMemo(() => {
    const groups = user?.profile?.['cognito:groups']?.map(g => g.toLowerCase()) || [];
    return groups.includes('admin') || groups.includes('manager') || String(user?.profile?.sub) === '1';
  }, [user]);

  const isAdmin = useMemo(() => {
    const groups = user?.profile?.['cognito:groups']?.map(g => g.toLowerCase()) || [];
    return groups.includes('admin') || String(user?.profile?.sub) === '1';
  }, [user]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = () => {
      if (isPrivilegedUser) {
        fetchAdminScanStats()
          .then(data => {
            if (isMounted) {
              setAdminScanStats(data || []);
              setLoading(false);
            }
          })
          .catch(error => {
            if (isMounted) {
              setAdminScanStats([]); // Set to empty array on error to stop loading
              setLoading(false);
            }
          });
      } else if (user?.profile?.sub) {
        fetchPatients()
          .then(data => {
            if (isMounted) {
              setPatients(data?.patients || []);
              setLoading(false);
            }
          })
          .catch(error => {
            if (isMounted) {
              setLoading(false);
            }
          });
      } else {
        setLoading(false); // No user, not loading
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [user, setPatients, isPrivilegedUser]);

  const groupStatsByMonth = (stats) => {
    if (!stats) return {};
    return stats.reduce((acc, stat) => {
      const month = formatFixedEastern(stat.date, { month: 'long', year: 'numeric' });
      if (!acc[month]) {
        acc[month] = [];
      }
      acc[month].push(stat);
      return acc;
    }, {});
  };

  const groupPatientsByMonth = (patients) => {
    if (!patients) return {};
    return patients.reduce((acc, patient) => {
      if (patient.documents) {
        patient.documents.forEach(doc => {
          const month = formatFixedEastern(doc.upload_date, { month: 'long', year: 'numeric' });
          if (!acc[month]) {
            acc[month] = [];
          }
          if (!acc[month].find(p => p.id === patient.id)) {
            acc[month].push(patient);
          }
        });
      }
      return acc;
    }, {});
  };

  const groupedAdminStats = useMemo(() => groupStatsByMonth(adminScanStats), [adminScanStats]);
  const groupedPatients = useMemo(() => groupPatientsByMonth(patients), [patients]);

  useEffect(() => {
    const months = Object.keys(isPrivilegedUser ? groupedAdminStats : groupedPatients);
    if (months.length > 0) {
      const sortedMonths = months.sort((a, b) => new Date(b) - new Date(a));
      if (!expandedMonth) {
        setExpandedMonth(sortedMonths[0]);
      }
    }
  }, [groupedAdminStats, groupedPatients, isPrivilegedUser, expandedMonth]);

  const handleMonthChange = (month) => {
    setExpandedMonth(expandedMonth === month ? null : month);
  };

  const handlePatientClick = (patientId) => {
    navigate(`/patient/${patientId}`);
  };

  const handleGenerateInvoice = (monthKey) => {
    const monthStats = groupedAdminStats[monthKey];
    if (!monthStats) return;

    const totalPages = monthStats
      .filter(stat => stat.employee_name.toLowerCase() !== 'sanduke')
      .reduce((acc, stat) => acc + stat.scanned_pages, 0);

    // Parse month and year from monthKey (e.g., 'May 2025' or 'May, 2025')
    const [monthName, yearStr] = monthKey.replace(',', '').split(' ');
    const monthNum = new Date(`${monthName} 1, 2000`).getMonth() + 1; // 1-indexed
    const yearNum = Number(yearStr);

    const now = new Date();
    const invoiceDay = now.getDate();

    navigate('/invoice', {
      state: {
        month: monthNum,
        year: yearNum,
        day: invoiceDay,
        pagesScanned: totalPages,
      },
    });
  };

  const renderAdminView = () => {
    if (!adminScanStats || adminScanStats.length === 0) {
      return <Typography>No scan statistics available.</Typography>;
    }

    const sortedMonths = Object.keys(groupedAdminStats).sort((a, b) => new Date(b) - new Date(a));

    return sortedMonths.map(month => {
      const monthStats = groupedAdminStats[month] || [];
      const filteredStats = monthStats.filter(stat => (isAdmin && showSanduke) || stat.employee_name.toLowerCase() !== 'sanduke');
      const totalPages = filteredStats.reduce((sum, stat) => sum + stat.scanned_pages, 0);

      const processedStats = filteredStats.map(stat => {
        const clockIn = new Date(stat.clock_in);
        const clockOut = new Date(stat.clock_out);
        const hoursWorked = ((clockOut - clockIn) / (1000 * 60 * 60)).toFixed(1);
        const pagesPerHour = (hoursWorked > 0 ? (stat.scanned_pages / hoursWorked) : 0).toFixed(1);

        return {
          date: formatFixedEastern(stat.date, { month: 'short', day: 'numeric' }),
          employee: stat.employee_name,
          clockIn: formatFixedEastern(stat.clock_in, { hour: 'numeric', minute: '2-digit', hour12: true }, false),
          clockOut: formatFixedEastern(stat.clock_out, { hour: 'numeric', minute: '2-digit', hour12: true }, false),
          hours: hoursWorked,
          pages: stat.scanned_pages,
          pagesPerHour: pagesPerHour
        };
      }).sort((a, b) => new Date(b.date) - new Date(a.date));

      return (
        <Accordion key={month} expanded={expandedMonth === month} onChange={() => handleMonthChange(month)} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 500 }}>{month}</Typography>
              <Typography sx={{ color: 'text.secondary', fontWeight: 500 }}>{totalPages.toLocaleString()} pages</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            {isPrivilegedUser && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGenerateInvoice(month);
                  }}
                  sx={{ fontWeight: 'bold' }}
                >
                  GENERATE INVOICE
                </Button>
              </Box>
            )}
            <Box sx={{ overflowX: 'auto' }}>
              <Paper sx={{ width: '100%', mb: 2 }}>
                <Box sx={{ display: 'table', width: '100%', minWidth: '800px', tableLayout: 'auto' }}>
                  <Box sx={{ display: 'table-header-group', bgcolor: '#f9f9f9' }}>
                    <Box sx={{ display: 'table-row' }}>
                      <Typography component="div" sx={{ p: 2, fontWeight: 'bold', borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap' }}>Date</Typography>
                      <Typography component="div" sx={{ p: 2, fontWeight: 'bold', borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap' }}>Employee</Typography>
                      <Typography component="div" sx={{ p: 2, fontWeight: 'bold', borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap', textAlign: 'right' }}>Pages</Typography>
                      <Typography component="div" sx={{ p: 2, fontWeight: 'bold', borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap', textAlign: 'right' }}>Scan Bonus</Typography>
                      <Typography component="div" sx={{ p: 2, fontWeight: 'bold', color: 'text.secondary', borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap' }}>First Scan</Typography>
                      <Typography component="div" sx={{ p: 2, fontWeight: 'bold', color: 'text.secondary', borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap' }}>Last Scan</Typography>
                      <Typography component="div" sx={{ p: 2, fontWeight: 'bold', color: 'text.secondary', borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap', textAlign: 'right' }}>Hours</Typography>
                      <Typography component="div" sx={{ p: 2, fontWeight: 'bold', borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap', textAlign: 'right' }}>Pages/hr</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'table-row-group' }}>
                    {processedStats.map((stat, index) => (
                      <Box key={index} sx={{ display: 'table-row', '&:hover': { bgcolor: '#f5f5f5' } }}>
                        <Typography component="div" sx={{ py: 2, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap' }}>{stat.date}</Typography>
                        <Typography component="div" sx={{ py: 2, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap' }}>{stat.employee}</Typography>
                        <Typography component="div" sx={{ py: 2, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', fontWeight: 'bold', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap', textAlign: 'right' }}>{stat.pages.toLocaleString()}</Typography>
                        <Typography component="div" sx={{ py: 2, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap', textAlign: 'right' }}>{`$${Math.floor(stat.pages / 100)}`}</Typography>
                        <Typography component="div" sx={{ py: 2, px: 2, color: 'text.secondary', borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap' }}>{stat.clockIn}</Typography>
                        <Typography component="div" sx={{ py: 2, px: 2, color: 'text.secondary', borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap' }}>{stat.clockOut}</Typography>
                        <Typography component="div" sx={{ py: 2, px: 2, color: 'text.secondary', borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap', textAlign: 'right' }}>{parseFloat(stat.hours).toLocaleString()}</Typography>
                        <Typography component="div" sx={{ py: 2, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', fontSize: '0.875rem', display: 'table-cell', whiteSpace: 'nowrap', textAlign: 'right' }}>{parseFloat(stat.pagesPerHour).toLocaleString()}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Paper>
            </Box>
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  const renderUserView = () => {
    if (!patients || patients.length === 0) {
      return <Typography>No patients found.</Typography>;
    }
    const sortedMonths = Object.keys(groupedPatients).sort((a, b) => new Date(b) - new Date(a));
    return sortedMonths.map(month => (
      <Accordion key={month} expanded={expandedMonth === month} onChange={() => handleMonthChange(month)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{`${month} (${groupedPatients[month].length} patients)`}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List dense>
            {groupedPatients[month].map(patient => (
              <ListItem key={patient.id} button onClick={() => handlePatientClick(patient.id)}>
                <ListItemText primary={`${patient.first_name} ${patient.last_name}`} secondary={`DOB: ${patient.dob}`} />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    ));
  };

  return (
    <Container maxWidth="lg" sx={{ marginTop: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Pages Scan Count
      </Typography>
      {isAdmin && (
        <FormControlLabel
          control={<Switch checked={showSanduke} onChange={(e) => setShowSanduke(e.target.checked)} name="showSanduke" />}
          label="Show 'sanduke' Employee"
          sx={{ mb: 2, display: 'block' }}
        />
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : (
        isPrivilegedUser ? renderAdminView() : renderUserView()
      )}
    </Container>
  );
};

export default PatientDatabase;
